// =============================================================================
// components/KpiIncidents/IncidentDetailPanel.jsx
//
// Collapsible bottom panel that animates into view when an incident is
// selected from the table. Layout: chart on the left, metrics on the right.
// =============================================================================
import {
	Box, Paper, Typography, IconButton, Tooltip,
	Chip, Divider, Collapse, Grid,
} from '@mui/material';
import CloseIcon          from '@mui/icons-material/Close';
import OpenInNewIcon      from '@mui/icons-material/OpenInNew';
import { TECH_COLOR, SEVERITY_OPTIONS } from '../../constants/kpiConstants';
import IncidentChart from './IncidentChart';

const SEV_MAP = SEVERITY_OPTIONS.reduce(function (acc, s) {
	acc[s.value] = s;
	return acc;
}, {});

function MetricRow({ label, value, mono }) {
	return (
		<Box sx={{
			display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
			py: 0.5, borderBottom: '1px dashed #f1f5f9',
			'&:last-child': { borderBottom: 'none' },
		}}>
			<Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
				{label}
			</Typography>
			<Typography variant="body2" sx={{
				fontSize: '0.78rem', fontWeight: 600,
				fontFamily: mono ? 'monospace' : 'inherit',
			}}>
				{value}
			</Typography>
		</Box>
	);
}

function fmtDate(d) {
	if (!d) return '—';
	try { return new Date(d).toLocaleDateString('en-GB'); } catch (e) { return '—'; }
}

function fmtNum(v, d) {
	if (v == null) return '—';
	if (d === undefined) d = 3;
	return Number(v).toFixed(d);
}

export default function IncidentDetailPanel({ incident, history, loading, onClose }) {
	const isOpen = incident != null;
	const techColor = incident ? (TECH_COLOR[incident.tech] || '#6366f1') : '#6366f1';
	const sev = incident ? SEV_MAP[incident.severity] : null;

	return (
		<Collapse in={isOpen} timeout={250} unmountOnExit>
			{incident && (
				<Paper variant="outlined" sx={{ overflow: 'hidden' }}>
					{/* Header */}
					<Box sx={{
						px: 2, py: 1.25,
						display: 'flex', alignItems: 'center', gap: 1.5,
						bgcolor: '#fafbfc',
						borderBottom: '1px solid #e5e7eb',
					}}>
						{/* Tech badge */}
						<Box sx={{
							px: 0.75, py: 0.25,
							bgcolor: techColor, color: 'white',
							fontSize: '0.7rem', fontWeight: 700,
							borderRadius: 0.5, minWidth: 30, textAlign: 'center',
						}}>
							{incident.tech}
						</Box>

						{/* Title */}
						<Box sx={{ flex: 1, minWidth: 0 }}>
							<Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.2 }} noWrap>
								{incident.kpiName}
							</Typography>
							<Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>
								{incident.vendor} · {incident.siteId} · {incident.cellId}
							</Typography>
						</Box>

						{/* Chips */}
						{sev && (
							<Chip
								label={sev.label}
								size="small"
								sx={{
									height: 22, fontSize: '0.7rem', fontWeight: 600,
									bgcolor: sev.color + '1f',
									color: sev.color,
									border: 'none',
								}}
							/>
						)}
						<Chip
							label={incident.detectionMethod || 'PERCENTILE'}
							size="small"
							variant="outlined"
							sx={{ height: 22, fontSize: '0.68rem' }}
						/>
						<Chip
							label={incident.status === 'Open' ? 'Open' : 'Closed'}
							size="small"
							sx={{
								height: 22, fontSize: '0.7rem', fontWeight: 600,
								bgcolor: incident.status === 'Open' ? '#fee2e2' : '#dcfce7',
								color:   incident.status === 'Open' ? '#991b1b' : '#166534',
								border: 'none',
							}}
						/>

						<Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

						<Tooltip title="Close panel">
							<IconButton size="small" onClick={onClose}>
								<CloseIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					</Box>

					{/* Body: chart + metrics */}
					<Grid container>
						<Grid item xs={12} md={8} sx={{
							p: 1.5,
							borderRight: { xs: 'none', md: '1px solid #e5e7eb' },
							borderBottom: { xs: '1px solid #e5e7eb', md: 'none' },
						}}>
							<IncidentChart
								incident={incident}
								history={history}
								loading={loading}
								height={260}
							/>
						</Grid>

						<Grid item xs={12} md={4} sx={{ p: 2 }}>
							<Typography variant="caption" color="text.secondary" sx={{
								display: 'block', mb: 1, textTransform: 'uppercase',
								letterSpacing: 0.5, fontSize: '0.68rem', fontWeight: 600,
							}}>
								Details
							</Typography>

							<MetricRow label="Current value" value={fmtNum(incident.kpiValue, 3)} mono />
							<MetricRow label="Trigger (P/SLA)" value={fmtNum(incident.triggerValue, 3)} mono />
							<MetricRow label="Contractual SLA" value={fmtNum(incident.slaThreshold, 2)} mono />
							<MetricRow
								label="Volume"
								value={incident.volume != null ? Math.round(incident.volume).toLocaleString('en-GB') : '—'}
								mono
							/>
							<MetricRow label="Direction" value={incident.direction ? 'Rise = bad' : 'Drop = bad'} />
							<MetricRow label="Duration" value={(incident.durationDays || 0) + ' days'} mono />
							<MetricRow label="Opened on" value={fmtDate(incident.startDate)} />
							<MetricRow label="Last seen" value={fmtDate(incident.lastSeen)} />

							{incident.comment && (
								<Box sx={{
									mt: 1.5, p: 1, borderRadius: 1,
									bgcolor: '#f8fafc', border: '1px solid #e5e7eb',
								}}>
									<Typography variant="caption" sx={{
										color: 'text.secondary', display: 'block',
										fontSize: '0.7rem', lineHeight: 1.4, fontStyle: 'italic',
									}}>
										{incident.comment}
									</Typography>
								</Box>
							)}
						</Grid>
					</Grid>
				</Paper>
			)}
		</Collapse>
	);
}
